import {
  FORMELEMENTS_ACCEPT_BUTTON,
  FORMELEMENTS_CHECKBOXES,
  FORMELEMENTS_DATEPICKER,
  FORMELEMENTS_RADIOBUTTONS,
  FORMELEMENTS_TEXTAREA
} from './FormElements';
import { isBoxesWidget } from '../helpers/formUtils';
import { arrPush } from '../helpers/immutable';
import II18 from '../translate/II18';

export const formWidgetHtmlId = item => `${item.id}_${item.liveKey}`;

export const formOptionHtmlId = (widget, option) =>
  `${
    widget.element === FORMELEMENTS_RADIOBUTTONS ? 'rad_' : ''
  }${formWidgetHtmlId(widget)}_${option.id}`;

export const formOptionInputHtmlId = (widget, option) =>
  `${formOptionHtmlId(widget, option)}_input`;
const layoutMaxSize = 12;
export const defineLayoutSize = size => size || layoutMaxSize;
/*export const defineLayoutWidth = layoutSize =>
  `calc(${(layoutSize / layoutMaxSize) * 100}% - ${
    layoutSize === layoutMaxSize ? 0 : 1
  }px)`;*/

export const getWidgetTooltip = (formItem, optionId) => {
  const boxesWidget = isBoxesWidget(formItem.element);
  const tooltipText = (formItem.tooltip && formItem.tooltip.trim()) || '';
  const mainText = tooltipText || formItem.label;
  return boxesWidget
    ? mainText + ' | ' + formItem.options.find(o => o.id === optionId).label
    : mainText || '';
};

/** Desktop PDF overlay: same text rules as the old createTooltips path. */
export const getAbsolutePlaceTooltipInfo = (formItem, option, errorMessage) => {
  if (errorMessage) {
    return { text: errorMessage, isError: true };
  }

  const formTooltip = (formItem.tooltip && formItem.tooltip.trim()) || '';
  const mainText = formTooltip || formItem.label || '';

  if (option) {
    const optionTooltip = (option.tooltip && option.tooltip.trim()) || '';
    return {
      text: optionTooltip || mainText + ' | ' + option.label,
      isError: false
    };
  }

  return { text: mainText, isError: false };
};

export const updateTabIndex = (formStructure, skipCheckBox) => {
  const hasCustomTabIndex = formStructure.some(
    item => item.tabIndex !== undefined
  );
  const widgetTabIndex = widget =>
    widget.tabIndex !== undefined
      ? widget.tabIndex
      : hasCustomTabIndex
      ? 9999
      : 0;
  formStructure.forEach(widget => {
    // eslint-disable-next-line default-case
    switch (widget.element) {
      case FORMELEMENTS_ACCEPT_BUTTON: {
        const element = document.getElementById(formWidgetHtmlId(widget));
        if (element) {
          element.tabIndex = widgetTabIndex(widget);
          element.onkeydown = event => {
            if (event.keyCode === 13) {
              event.preventDefault();
              element.click();
            }
          };
        }
        break;
      }
      case FORMELEMENTS_RADIOBUTTONS:
      case FORMELEMENTS_CHECKBOXES: {
        if (!skipCheckBox || widget.element !== FORMELEMENTS_CHECKBOXES) {
          widget.options.forEach(option => {
            const element = document.getElementById(
              formOptionHtmlId(widget, option)
            );
            if (element) {
              element.tabIndex = widgetTabIndex(widget);
              element.onkeydown = event => {
                if (event.keyCode === 32) {
                  event.preventDefault();
                  element.children[0].click();
                }
              };
            }
          });
        }
        break;
      }
      //no-default
    }
  });
};

const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 42;

export const checkIfValueInBound = (min, value, max) => {
  return Math.min(Math.max(value, min), max);
};

const pixelWidth = require('string-pixel-width');

/*export const calcWidgetFontSize = (
  text,
  scaledWidth,
  scaledHeight,
  scale,
  customFontSize
) => {
  let fontSize = customFontSize ? customFontSize * scale : false;

  if (!fontSize) {
    const maxVerticalFontSize = scaledHeight - scaledHeight * (2 / 5);

    const estimatedFontWidth =
      pixelWidth(text, { font: 'Arial', size: maxVerticalFontSize }) * 1.1;
    const maxHorizontalFontSize =
      maxVerticalFontSize *
      (estimatedFontWidth ? scaledWidth / estimatedFontWidth : 1);
    fontSize = clamp(
      MIN_FONT_SIZE * scale,
      maxHorizontalFontSize,
      Math.min(maxVerticalFontSize, MAX_FONT_SIZE * scale)
    );
  }
  return fontSize;
};*/

export function widgetTextMaxHeight(height) {
  return height - height * (1 / 5);
}

export function widgetTextMinHeight(height) {
  return height / 4;
}

export function breakTextInRect(
  fontSize,
  text,
  scaledWidth,
  scaledHeight,
  scale
) {
  const textWidth = pixelWidth(text, { font: 'Arial', size: fontSize }) * 1.1;
  const breaks = textWidth / (scaledWidth - 20 * scale);

  if (breaks < 1) return [text];

  const maxPartLength = Math.round(text.length / breaks);
  if (maxPartLength <= 1) return [text];
  const result = [];

  let rest = text;
  let breakPosition = maxPartLength;
  while (rest.length > maxPartLength) {
    while (breakPosition && rest[breakPosition] !== ' ') {
      breakPosition--;
    }
    if (breakPosition === 0) {
      result.push(rest.slice(0, maxPartLength));
      rest = rest.slice(maxPartLength, rest.length);
    } else {
      result.push(rest.slice(0, breakPosition));
      rest = rest.slice(breakPosition + 1, rest.length);
    }
    breakPosition = maxPartLength;
  }
  result.push(rest);

  return result;
}

export function calcWidgetFontSize(
  text,
  scaledWidth,
  scaledHeight,
  scale,
  customFontSize,
  checkOutOfBound,
  _minFontSize
) {
  let fontSize = customFontSize ? customFontSize * scale : false;
  const padding = 20;

  if (!fontSize) {
    const minPossibleFontSize =
      (_minFontSize ? _minFontSize : MIN_FONT_SIZE) * scale;
    const maxPossibleFontSize = MAX_FONT_SIZE * scale;

    const maxFontSize = Math.min(
      widgetTextMaxHeight(scaledHeight),
      maxPossibleFontSize
    );
    const minFontSize = Math.max(
      Math.min(widgetTextMinHeight(scaledHeight), maxPossibleFontSize),
      minPossibleFontSize
    );

    const widthOfTextWithMaxWidgetFontSize =
      pixelWidth(text, { font: 'Arial', size: maxFontSize }) * 1.1;

    const calculatedFontSize =
      maxFontSize *
      (widthOfTextWithMaxWidgetFontSize
        ? (scaledWidth - padding * scale) / widthOfTextWithMaxWidgetFontSize
        : 1);

    fontSize = checkIfValueInBound(
      minFontSize,
      calculatedFontSize,
      maxFontSize
    );

    if (
      checkOutOfBound &&
      pixelWidth(text, { font: 'Arial', size: fontSize }) >
        scaledWidth - padding * scale
    ) {
      const brokenText = breakTextInRect(
        fontSize,
        text,
        scaledWidth,
        scaledHeight,
        scale
      );
      if (brokenText.length > 1) {
        fontSize = checkIfValueInBound(
          minPossibleFontSize,
          (scaledHeight - scaledHeight / 6) / (brokenText.length * 1.3),
          maxFontSize
        );
      }
    }
  }

  return fontSize;
}

export const calcWidgetMonospace = (
  originalWidth,
  originalHeight,
  scale,
  maxLength
) => {
  const scaledWidth = originalWidth * scale;
  const fSize = Math.min(MAX_FONT_SIZE, originalHeight - 6 * scale) * scale;

  const placeWidth = (scaledWidth - 1) / maxLength;
  const letterWidth = fSize / 1.8;
  const letterSpace = placeWidth - letterWidth;

  return {
    fontSize: fSize,
    fontFamily: 'monospace',
    letterSpacing: letterSpace,
    paddingLeft: letterSpace / 3
  };
};

const TEXT_AREA_FONT_SIZE = 12;
const TEXT_AREA_FONT_SIZE_ALT = 22;
//const ACCEPT_BUTTON_FONT_SIZE = 14;
//const ACCEPT_BUTTON_FONT_SIZE_ALT = 24;

export const getAbsPlaceStyle = (item, scale, value, dpiModifier) => {
  const newWidth = item.width * scale.scaleX;
  const newHeight = item.height * scale.scaleY;

  let text = value;

  let customFontSize = null;
  let minFontSize = null;

  if (item.element === FORMELEMENTS_DATEPICKER) {
    text = displayDate(text);
  }

  if (item.element === FORMELEMENTS_ACCEPT_BUTTON) {
    text = item.label;
    minFontSize = dpiModifier ? 10 : 18;
    /*customFontSize = dpiModifier
      ? ACCEPT_BUTTON_FONT_SIZE
      : ACCEPT_BUTTON_FONT_SIZE_ALT;*/
  }

  if (item.element === FORMELEMENTS_TEXTAREA) {
    customFontSize = dpiModifier
      ? TEXT_AREA_FONT_SIZE
      : TEXT_AREA_FONT_SIZE_ALT;
    //customFontSize = pageHeight / 80;
  }

  const fontSize = isBoxesWidget(item.element)
    ? newHeight
    : calcWidgetFontSize(
        text,
        newWidth,
        newHeight,
        scale.scaleY,
        item.textStyle?.fontSize || customFontSize,
        item.element === FORMELEMENTS_ACCEPT_BUTTON,
        minFontSize
      );

  return {
    //borderWidth: 2,
    top: item.y * scale.scaleY,
    left: item.x * scale.scaleX,
    width: newWidth,
    height: newHeight,
    fontSize: fontSize,
    lineHeight:
      item.element === FORMELEMENTS_ACCEPT_BUTTON ? fontSize * 1.1 + 'px' : null
  };
};

export function checkDateAndGetISODate(string) {
  if (!string) return '';
  if (isNaN(Date.parse(string))) return '';

  return prepareLocaleOrUTCDate(string);
}

export function displayDate(string) {
  if (!string) return '';
  if (/(\d{2})\.(\d{2})\.(\d{4})/.test(string)) return string; //for old or converted values

  return isNaN(Date.parse(string)) ? '' : displayLocaleOrUTCDate(string);
}

export const findCurrentPageElementById = (formStructure, elementId) => {
  return formStructure.find(item => item.id === elementId);
};

export function toggleBoxesValue(currentValue, optionId) {
  let result = currentValue;
  if (currentValue.includes(optionId)) {
    result = currentValue.filter(id => id !== optionId);
  } else {
    result = arrPush(result, optionId);
  }
  //console.log(result);
  return result;
}

/*export const getBoxesValue = (elementId, pageNumber) => {
  const checkboxes = Array.from(
    document.querySelectorAll('input[name="' + elementId + '"]:checked')
  );
  return checkboxes ? checkboxes.map(option => option.value) : [];
};*/

export function getTodayUTCDate() {
  return prepareDateToFormData(new Date());
}

export function displayLocaleOrUTCDate(str) {
  return new Date(prepareLocaleOrUTCDate(str)).toLocaleDateString(
    II18.locale(),
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }
  );
}

export function prepareLocaleOrUTCDate(str) {
  const date = new Date(str);
  if (date.toString() === 'Invalid Date') return '';

  if (str.indexOf('T00') !== -1) {
    return prepareUTCDateString(date);
  } else {
    return prepareDateString(date);
  }
}

export function prepareUTCDateString(date) {
  const data = getDateUTCData(date);
  return data.year + '-' + data.mon + '-' + data.day;
}
export function prepareDateString(date) {
  const data = getDateData(date);
  return data.year + '-' + data.mon + '-' + data.day;
}

const pad = number => (number < 10 ? '0' + number : number);
export function getDateUTCData(date) {
  return {
    day: pad(date.getUTCDate()),
    mon: pad(date.getUTCMonth() + 1),
    year: date.getUTCFullYear()
  };
}

export function getDateData(date) {
  return {
    day: pad(date.getDate()),
    mon: pad(date.getMonth() + 1),
    year: date.getFullYear()
  };
}

export const prepareDateToFormData = dateStr => {
  dateStr =
    typeof dateStr === 'object' && dateStr !== null
      ? dateStr.toDateString() + ' UTC'
      : dateStr;
  return dateStr ? new Date(dateStr).toISOString() : '';
};
